import { action } from 'satcheljs';
import type { OwaDate } from 'owa-datetime';
import type { FreeBusyCacheManager, FreeBusyType } from 'owa-freebusy-cache';
import type {
    Room,
    RoomList,
    RoomSelectorFilters,
    CalendarEventTime,
} from '../components/RoomSelector/RoomSelector.types';
import type { RoomRecurrenceAvailability } from '../store/schema/RoomAvailabilityState';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type { OpxRoomFinderData } from '../store/schema/RoomPanelOpxTypes';
import type RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import type RoomListViewState from '../store/schema/RoomListViewState';

/**
 * Initialize RoomPanel view state
 */
export const initRoomPanelViewState = action(
    'initRoomPanelViewState',
    (viewStateId: string | null, roomListViewState: RoomListViewState) => ({
        viewStateId: viewStateId,
        roomListViewState: roomListViewState,
    })
);

/**
 * Remove RoomPanel view state
 */
export const removeRoomPanelViewState = action(
    'removeRoomPanelViewState',
    (viewStateId: string | null) => ({
        viewStateId,
    })
);

/**
 * Gets all room lists
 */
export const initializeRoomList = action(
    'initializeRoomList',
    (
        isConsumer: boolean,
        eventStartDateTime: OwaDate,
        eventEndDateTime: OwaDate,
        isAllDayEvent: boolean,
        recurrence: RecurrenceType,
        requiredAttendees: AttendeeType[],
        optionalAttendees: AttendeeType[],
        originalEvent: CalendarEvent,
        viewStateId: string | null
    ) => ({
        isConsumer,
        eventStartDateTime,
        eventEndDateTime,
        isAllDayEvent,
        recurrence,
        requiredAttendees,
        optionalAttendees,
        originalEvent,
        viewStateId,
    })
);

/**
 * Gets all the rooms from a given room list
 */
export const getRoomsForList = action(
    'getRoomsForList',
    (roomlistSmtp: string, viewStateId: string | null) => ({
        roomlistSmtp: roomlistSmtp,
        viewStateId: viewStateId,
    })
);

/**
 * Action that updates the user room config settings in the store
 */
export const updateUserRoomListConfig = action(
    'updateUserRoomListConfig',
    (
        userCurrentRoomListName: string,
        userCurrentRoomListEmail: string,
        viewStateId: string | null
    ) => ({
        userCurrentRoomListName: userCurrentRoomListName,
        userCurrentRoomListEmail: userCurrentRoomListEmail,
        viewStateId: viewStateId,
    })
);

/**
 * Action that updates the list of rooms settings in the store
 */
export const updateRoomsInStore = action(
    'updateRoomsInStore',
    (rooms: Room[], viewStateId: string | null) => ({
        rooms: rooms,
        viewStateId: viewStateId,
    })
);

/**
 * Action that updates the list of room lists settings in the store
 */
export const updateRoomListsInStore = action(
    'updateRoomListsInStore',
    (roomLists: RoomList[], viewStateId: string | null) => ({
        roomLists: roomLists,
        viewStateId: viewStateId,
    })
);

/**
 * Action that updates the list of preferred room lists settings in the store
 */
export const updatePreferredRoomListsInStore = action(
    'updatePreferredRoomListsInStore',
    (preferredRoomLists: RoomList[], viewStateId: string | null) => ({
        preferredRoomLists: preferredRoomLists,
        viewStateId: viewStateId,
    })
);

/**
 * Action that updates the user room config settings in the service
 */
export const setUserRoomListConfig = action(
    'setUserRoomListConfig',
    (roomFilters: RoomSelectorFilters, viewStateId: string | null) => ({
        roomFilters: roomFilters,
        viewStateId: viewStateId,
    })
);

/**
 * Gets all the rooms, given the passed in filter, from the Places API
 */
export const searchRoomsUsingPlacesApi = action(
    'searchRoomsUsingPlacesApi',
    (roomFilters: RoomSelectorFilters, viewStateId: string | null) => ({
        roomFilters: roomFilters,
        viewStateId: viewStateId,
    })
);

/**
 * Updates the rooms search state (wheather a search is in progress or not)
 */
export const updateSearchStateInStore = action(
    'updateSearchStateInStore',
    (isSearching: boolean, viewStateId: string | null) => ({
        isSearching: isSearching,
        viewStateId: viewStateId,
    })
);

export const fetchRoomsAvailabilityForRoomPanel = action(
    'fetchRoomsAvailabilityForRoomsPanel',
    (
        eventStartDate: OwaDate,
        eventEndDate: OwaDate,
        isAllDayEvent: boolean,
        recurrence: RecurrenceType,
        requiredAttendees: AttendeeType[],
        optionalAttendees: AttendeeType[],
        originalEvent: CalendarEvent,
        viewStateId: string | null
    ) => ({
        eventStartDate,
        eventEndDate,
        isAllDayEvent,
        recurrence,
        requiredAttendees,
        optionalAttendees,
        originalEvent,
        viewStateId,
    })
);

/*
 * fetches availability for favorite rooms
 */
export const fetchRoomsAvailability = action(
    'fetchRoomsAvailability',
    (
        rooms: Room[],
        freeBusyCache: FreeBusyCacheManager,
        eventStartDateTime: OwaDate,
        eventEndDateTime: OwaDate,
        isAllDayEvent: boolean,
        recurrence: RecurrenceType,
        requiredAttendees: AttendeeType[],
        optionalAttendees: AttendeeType[],
        filter?: CalendarEvent
    ) => ({
        rooms,
        freeBusyCache,
        eventStartDateTime,
        eventEndDateTime,
        isAllDayEvent,
        recurrence,
        requiredAttendees,
        optionalAttendees,
        filter,
    })
);

/**
 * Get room availability using scheduling getRoomAvailability API, for the roomEmail
 */
export const getRoomAvailability = action(
    'getRoomAvailability',
    (roomEmail: string, calendarEventTime: CalendarEventTime) => ({
        roomEmail: roomEmail,
        calendarEventTime: calendarEventTime,
    })
);

/**
 * Updates a room availabiliy results for the roomEmail
 */
export const updateRoomAvailabilityResults = action(
    'updateRoomAvailabilityResults',
    (
        roomEmail: string,
        availability: FreeBusyType,
        roomRecurrenceAvailability: RoomRecurrenceAvailability,
        requestedCalendarTime: CalendarEventTime,
        availabilityTimestamp: number
    ) => ({
        roomEmail: roomEmail,
        availability: availability,
        roomRecurrenceAvailability: roomRecurrenceAvailability,
        requestedCalendarTime: requestedCalendarTime,
        availabilityTimestamp: availabilityTimestamp,
    })
);

/**
 * Updates the OPX room finder data
 */
export const updateOpxRoomFinderData = action(
    'updateOpxRoomFinderData',
    (data: OpxRoomFinderData) => ({
        data,
    })
);

export const initializeRoomListPicker = action(
    'initializeRoomListPicker',
    (
        roomLists: RoomList[],
        citiesAndRoomLists: { [city: string]: RoomList[] },
        viewStateId: string | null
    ) => ({
        roomLists,
        citiesAndRoomLists,
        viewStateId,
    })
);
